'use client';

import { useState, useEffect, useRef } from 'react';

const parseContent = (decodedHtml: string) => {
  if (typeof document === 'undefined') {
    return decodedHtml;
  }

  try {
    const temporaryDiv = document.createElement('div');
    temporaryDiv.innerHTML = decodedHtml;
    return temporaryDiv.textContent || '';
  } catch (error) {
    console.error('Error parsing content:', error);
    return decodedHtml;
  }
};

type UseTypewriterProps = {
  content: string;
  onHalfway?: () => void;
  onComplete?: () => void;
  delay?: number;
};

const useTypewriter = ({ content, onHalfway, onComplete, delay }: UseTypewriterProps) => {
  const parsedContent = parseContent(content);

  const [displayText, setDisplayText] = useState('');
  const [index, setIndex] = useState(0);
  const [isCursor, setIsCursor] = useState(true);
  const [isHalfwayReached, setIsHalfwayReached] = useState(false);

  const intervalReference = useRef<null | ReturnType<typeof setInterval>>(null);
  const timerReference = useRef<null | ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    setDisplayText('');
    setIndex(0);
    setIsCursor(true);
    setIsHalfwayReached(false);

    if (intervalReference.current) {
      clearInterval(intervalReference.current);
      intervalReference.current = null;
    }

    if (timerReference.current) {
      clearTimeout(timerReference.current);
      timerReference.current = null;
    }
  }, [content]);

  useEffect(() => {
    if (!parsedContent || isHalfwayReached) return;

    const maxCountLength = parsedContent.length > 480 ? 120 : parsedContent.length / 4;

    if (onHalfway && index >= maxCountLength && !isHalfwayReached) {
      if (intervalReference.current) {
        clearInterval(intervalReference.current);
        intervalReference.current = null;
      }

      setIsCursor(false);
      setIsHalfwayReached(true);
      timerReference.current = setTimeout(() => {
        onHalfway && onHalfway();
      }, delay);

      return;
    }

    const randomDelay = Math.floor(Math.random() * 51) + 50;
    const randomStep = Math.floor(Math.random() * 5) + 1;

    intervalReference.current = setInterval(() => {
      const nextIndex = Math.min(index + randomStep, parsedContent.length);
      setDisplayText(parsedContent.slice(0, nextIndex));
      setIndex(nextIndex);

      if (nextIndex === parsedContent.length) {
        intervalReference.current && clearInterval(intervalReference.current);
        intervalReference.current = null;

        setIsCursor(false);
        onComplete && onComplete();
      }
    }, randomDelay);

    return () => {
      if (timerReference.current) {
        clearTimeout(timerReference.current);
      }
      if (intervalReference.current) {
        clearInterval(intervalReference.current);
      }
    };
  }, [content, index]);

  return {
    displayText,
    isCursor
  };
};

export default useTypewriter;
